<template>
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    :nudge-width="200"
    offset-y
    color="success"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-bind="attrs" elevation="3" v-on="on" :small="small" :block="$isMobile()" class="ma-2">
        <v-icon class="mr-1" color="blue">mdi-tune</v-icon>
        {{ $lang('calendar.settings.title') }}
        <v-icon>mdi-menu-down</v-icon>
      </v-btn>
    </template>

    <v-card max-width="450">
      <v-card-title>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        {{ $lang('calendar.settings.view') }}
        <v-spacer></v-spacer>
        <v-switch
          v-show="filters.view !== $const('ConstCalendarViews', 'BY_UNITS')"
          v-model="filters.is_compact"
          :label="$lang('calendar.settings.is_compact')"
          @change="changeSettings('is_compact')"
        />
      </v-card-title>
      <v-card-text>
        <v-btn-toggle
          v-model="filters.days"
          @change="changeSettings('days')"
          mandatory
        >
          <v-btn :value="1">
            {{$lang('calendar.settings.days.1day')}}
          </v-btn>
          <v-btn :value="3">
            {{$lang('calendar.settings.days.3days')}}
          </v-btn>
          <v-btn :value="7">
            {{$lang('calendar.settings.days.7days')}}
          </v-btn>
          <v-btn :value="14">
            {{$lang('calendar.settings.days.14days')}}
          </v-btn>
          <v-btn :value="31">
            {{$lang('calendar.settings.days.month')}}
          </v-btn>
        </v-btn-toggle>
        <v-radio-group
          v-model="filters.view"
          @change="changeSettings('view')"
          dense
          hide-details
        >
          <v-radio
            v-for="(type, t) in $in('ConstCalendarViews')" :key="t"
            :value="type.value"
            :label="type.text"
          />
        </v-radio-group>
        <v-boolean
          v-model="filters.show_legend"
          :label="$lang('calendar.settings.show_legend')"
          @change="changeSettings('show_legend')"
          dense
          hide-details
          prepend-icon="mdi-map-legend"
        />
        <v-boolean
          v-model="filters.show_ruler"
          :label="$lang('calendar.settings.show_ruler')"
          @change="changeSettings('show_ruler')"
          dense
          hide-details
          prepend-icon="mdi-ruler"
        />
      </v-card-text>

      <v-toolbar flat color="blue darken-1" dark>
        <v-switch
          class="mb-0"
          :messages="$lang('calendar.hints.with_reservations')"
          v-model="filters.with_reservations"
          @change="changeSettings('with_reservations')"
          :label="$lang('calendar.settings.with_reservations')"
        ></v-switch>
      </v-toolbar>

      <v-card-text v-show="filters.with_reservations">
        <v-row>
          <v-col cols="6">
            <v-select
              v-model="filters.color_by"
              :items="colorBy"
              :label="$lang('calendar.settings.color_by.title')"
              @click="changeRateOption('color_by')"
              dense
              prepend-icon="mdi-palette"
              clearable
            />
          </v-col>
          <v-col cols="6">
            <v-boolean 
              v-model="filters.is_reservation_shaped"
              :label="$lang('calendar.settings.is_reservation_shaped')"
              @click="changeRateOption('is_reservation_shaped')"
            />
          </v-col>
        </v-row>
        <div class="text-overline">{{ $lang('calendar.settings.choose_options') }}</div>
        <v-chip-group column multiple v-model="reservationOptions">
          <v-chip
            filter
            outlined
            value="show_client_name"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_client_name')"
            :color="filters.show_client_name ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-account</v-icon>
            {{ $lang('calendar.settings.show_client_name') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_icons"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_icons')"
            :color="filters.show_icons ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-brain </v-icon>
            {{ $lang('calendar.settings.smart_icons') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_contacts"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_contacts')"
            :color="filters.show_contacts ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-account-details </v-icon>
            {{ $lang('calendar.settings.client_contacts') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_checkin_status"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_checkin_status')"
            :color="filters.show_checkin_status ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-home-circle </v-icon>
            {{ $lang('calendar.settings.checkin_status') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="with_extras"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('with_extras')"
            :color="filters.with_extras ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-checkbox-multiple-blank </v-icon>
            {{ $lang('calendar.settings.extra') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="with_bills"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('with_bills')"
            :color="filters.with_bills ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-calculator-variant</v-icon>
            {{ $lang('calendar.settings.with_bills') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_arrival_time"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_arrival_time')"
            :color="filters.show_arrival_time ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-send-clock </v-icon>
            {{ $lang('calendar.settings.arrival_time') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="with_notes"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('with_notes')"
            :color="filters.with_notes ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-code-not-equal </v-icon>
            {{ $lang('calendar.settings.with_notes') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_ota_image"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_ota_image')"
            :color="filters.show_ota_image ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-image</v-icon>
            {{ $lang('calendar.settings.show_ota_image') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_amounts"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_amounts')"
            :color="filters.show_amounts ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-cash</v-icon>
            {{ $lang('calendar.settings.show_amounts') }}
          </v-chip>
          <v-chip
            filter
            outlined
            value="show_housekeeping"
            :disabled="!filters.with_reservations"
            @click="changeReservationOption('show_housekeeping')"
            :color="filters.show_housekeeping ? 'blue darken-1' : 'blue lighten-2'"
          >
            <v-icon left> mdi-spray-bottle</v-icon>
            {{ $lang('calendar.settings.show_housekeeping') }}
          </v-chip>
        </v-chip-group>
      </v-card-text>

      <v-toolbar v-if="$can('VIEW_RATE_CALENDAR')" flat color="teal darken-1" dark>
        <v-switch
          class="mb-0"
          :messages="$lang('calendar.hints.with_rates')"
          v-model="filters.with_rates"
          @change="changeSettings('with_rates')"
          :label="$lang('calendar.settings.with_rates')"
        ></v-switch>
      </v-toolbar>
      <v-fab-transition v-if="$can('VIEW_RATE_CALENDAR')">
        <v-card-text v-show="!isDisabledRateOptions">
          <div class="text-overline">{{ $lang('calendar.settings.choose_options') }}</div>
          <v-chip-group column multiple v-model="rateOptions">
            <v-chip
              filter
              outlined
              value="show_restrictions"
              :disabled="!filters.with_rates"
              @click="changeRateOption('show_restrictions')"
              :color="filters.show_restrictions ? 'teal darken-1' : 'teal lighten-2'"
            >
              <v-icon left> mdi-dots-vertical-circle </v-icon>
              {{ $lang('calendar.settings.show_restrictions') }}
            </v-chip>
            <v-chip
              filter
              outlined
              value="show_length_of_stay"
              :disabled="!filters.with_rates"
              @click="changeRateOption('show_length_of_stay')"
              :color="filters.show_length_of_stay ? 'teal darken-1' : 'teal lighten-2'"
            >
              <v-icon left> mdi-weather-night </v-icon>
              {{ $lang('calendar.settings.show_length_of_stay') }}
            </v-chip>
            <v-chip
              filter
              outlined
              value="show_not_sellable"
              :disabled="!filters.with_rates"
              @click="changeRateOption('show_not_sellable')"
              :color="filters.show_not_sellable ? 'teal darken-1' : 'teal lighten-2'"
            >
              <v-icon left> mdi-calendar-multiselect </v-icon>
              {{ $lang('calendar.settings.show_not_sellable') }}
            </v-chip>
            <v-chip
              filter
              outlined
              value="show_availability"
              :disabled="!filters.with_rates"
              @click="changeRateOption('show_availability')"
              :color="filters.show_availability ? 'teal darken-1' : 'teal lighten-2'"
            >
              <v-icon left> mdi-counter </v-icon>
              {{ $lang('calendar.settings.show_availability') }}
            </v-chip>
            <v-chip
              filter
              outlined
              value="show_hidden_rate_plans"
              :disabled="!filters.with_rates"
              @click="changeRateOption('show_hidden_rate_plans')"
              :color="filters.show_hidden_rate_plans ? 'teal darken-1' : 'teal lighten-2'"
            >
              <v-icon left> mdi-eye-off </v-icon>
              {{ $lang('calendar.settings.show_hidden_rate_plans') }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </v-fab-transition>
    </v-card>
  </v-menu>
</template>

<script>
  import calendar from "@/pages/calendar/mixins/calendar";

  const rateOptions = [
    'show_restrictions',
    'show_length_of_stay',
    'show_not_sellable',
    'show_availability',
    'show_hidden_rate_plans',
    'show_legend',
  ];

  const reservationOptions = [
    'with_extras',
    'with_bills',
    'with_notes',
    'show_icons',
    'show_contacts',
    'show_checkin_status',
    'show_arrival_time',
    'show_ota_image',
    'show_amounts',
    'show_housekeeping',
    'show_client_name',
    'color_by',
  ];

  export default {
    name: "SettingsMenu",
    props: {
      small: {
        type: Boolean,
        default: false,
      }
    },
    mixins: [
      calendar
    ],
    data() {
      return {
        isMenuOpen: false,
        colorBy: [],
      }
    },
    beforeMount() {
      this.colorBy = [
        {
          value: 'paid',
          text: this.$lang('calendar.settings.color_by.paid')
        },
        {
          value: 'bills',
          text: this.$lang('calendar.settings.color_by.bills')
        },
        {
          value: 'checkin',
          text: this.$lang('calendar.settings.color_by.checkin')
        },
      ]
    },
    methods: {
      changeRateOption(option) {
        this.filters[option] = this.rateOptions.indexOf(option) < 0;
        this.changeSettings(option);
      },
      changeReservationOption(option) {
        this.filters[option] = this.reservationOptions.indexOf(option) < 0;
        this.changeSettings(option);
      },
      close() {
        this.isMenuOpen = false;
      }
    },
    computed: {
      rateOptions: {
        get() {
          return rateOptions.filter(option => this.filters[option]);
        },
        set() {}
      },
      reservationOptions: {
        get() {
          return reservationOptions.filter(option => this.filters[option]);
        },
        set() {}
      },
      isDisabledRateOptions() {
        return this.filters.view === this.$const('ConstCalendarViews', 'BY_UNITS') ||
          this.filters.view === this.$const('ConstCalendarViews', 'BY_PROPERTY_UNITS');
      }
    },
  };
</script>
